import { usePostHog } from 'posthog-js/react'
import { captureMessage } from '@sentry/react'
import { useCallback, useRef } from 'react'
import { useLocation } from 'react-router'

import { FUNNEL_EVENTS } from 'app/tracking/events'
import { buildOnfidoStepCompletedProperties, sanitizeOnfidoLegacyProperties } from 'app/tracking/onfidoStepAnalytics'
import { ANALYTICS_EVENTS, ANALYTICS_EVENTS_KEY, SCREEN_NAMES } from 'app/utils/constants'

/** Payload keys set by `trackClick` before merging caller properties; callers cannot override these. */
const TRACK_CLICK_RESERVED_PAYLOAD_KEYS = new Set<string>([ANALYTICS_EVENTS_KEY.OBJECT_CLICKED])

/**
 * Custom hook for tracking user interactions with PostHog analytics
 * @returns {Object} Object containing tracking functions
 * @returns {Function} returns.trackClick - Function to track click events
 */
export default function useTracking() {
    const posthog = usePostHog()
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const location = useLocation()

    const pathname = location.pathname.split('/').pop()
    const screenName = SCREEN_NAMES[pathname as keyof typeof SCREEN_NAMES]

    /**
     * Debounced function to track slider changes with PostHog analytics
     * Uses a 300ms debounce to prevent excessive tracking calls during rapid slider adjustments
     * @param {string} sliderName - The identifier of the slider being changed
     * @param {number} value - The new value of the slider
     */
    const debouncedTrackSliderChange = useCallback(
        (sliderName: string, value: number) => {
            // Clear any existing timeout to prevent multiple rapid calls
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }

            // Debounce slider tracking to 300ms to avoid excessive analytics calls
            debounceTimeoutRef.current = setTimeout(() => {
                posthog?.capture(`${screenName} / ${ANALYTICS_EVENTS.SLIDER_CHANGED}`, {
                    [ANALYTICS_EVENTS_KEY.SLIDER_NAME]: sliderName,
                    [ANALYTICS_EVENTS_KEY.SLIDER_VALUE]: value
                })
            }, 300)
        },
        [posthog, screenName]
    )

    return {
        /**
         * Tracks a click event with the specified object identifier
         * @param {string} object - The identifier of the clicked object (stable; avoid dynamic text in the event name)
         * @param {Record<string, string | number | boolean | null>} [properties] - Optional PostHog event properties (e.g. payment date as ISO string)
         */
        trackClick: (object: string, properties?: Record<string, string | number | boolean | null>) => {
            const payload: Record<string, unknown> = {
                [ANALYTICS_EVENTS_KEY.OBJECT_CLICKED]: object
            }
            if (properties) {
                for (const [key, value] of Object.entries(properties)) {
                    if (value === undefined || TRACK_CLICK_RESERVED_PAYLOAD_KEYS.has(key)) {
                        continue
                    }
                    payload[key] = value
                }
            }
            posthog?.capture(`${screenName} / ${object} ${ANALYTICS_EVENTS.CLICKED}`, payload)
        },
        /**
         * Tracks a slider change event with the specified slider name and value
         * Debounced to prevent excessive tracking calls during rapid slider adjustments
         * @param {string} sliderName - The name/identifier of the slider being changed
         * @param {number} value - The new value of the slider
         */
        trackSliderChange: debouncedTrackSliderChange,
        /**
         * Tracks an Onfido SDK analytics event.
         *
         * Emits the legacy screen-scoped event for backward compatibility and, when
         * applicationId is provided, the flattened `onfido_step_completed` funnel event.
         * When applicationId is missing, reports a Sentry warning so we can audit
         */
        trackOnfidoEvent: (eventName: string, eventProperties: Record<string, unknown> = {}, applicationId?: string) => {
            const sanitizedLegacyProperties = sanitizeOnfidoLegacyProperties(eventProperties)

            try {
                posthog?.capture(`${screenName} / ${ANALYTICS_EVENTS.ONFIDO_EVENT}`, {
                    [ANALYTICS_EVENTS_KEY.ONFIDO_EVENT_NAME]: eventName,
                    [ANALYTICS_EVENTS_KEY.ONFIDO_EVENT_PROPERTIES]: sanitizedLegacyProperties
                })
            } catch (error) {
                captureMessage('Failed to capture legacy Onfido event', {
                    level: 'error',
                    extra: { error }
                })
            }

            if (!applicationId) {
                // Audit legacy-only sends so we can safely remove the legacy event later.
                captureMessage('Skipped onfido_step_completed: missing applicationId', {
                    level: 'warning',
                    extra: { eventName, screenName }
                })
                return
            }

            try {
                posthog?.capture(FUNNEL_EVENTS.ONFIDO_STEP_COMPLETED, buildOnfidoStepCompletedProperties(applicationId, eventName, eventProperties))
            } catch (error) {
                captureMessage('Failed to capture onfido_step_completed', {
                    level: 'error',
                    extra: { error }
                })
            }
        },

        trackDropdownSelect: (dropdownName: string, value: string) => {
            posthog?.capture(`${screenName} / ${ANALYTICS_EVENTS.DROPDOWN_SELECTED}`, {
                [ANALYTICS_EVENTS_KEY.DROPDOWN_NAME]: dropdownName,
                [ANALYTICS_EVENTS_KEY.DROPDOWN_VALUE]: value
            })
        }
    }
}
